import React, { createContext, ReactNode } from 'react';

import { CreateReportSidebar } from '@futrli/predict/report-builder/components/create-report-sidebar/create-report-sidebar';
import { CreateFutrliTemplateSidebar } from '@futrli/predict/report-builder/components/create-futrli-template-sidebar/create-futrli-template-sidebar';
import { CreateTemplateSidebar } from '@futrli/predict/report-builder/components/create-template-sidebar/create-template-sidebar';
import {
  DriverBuilderSidebar,
  DriverBuilderSidebarData
} from '@futrli/predict/non-financial-drivers/sidebars/driver-builder-sidebar';
import { DuplicateReportSidebar } from '@futrli/predict/reports/components/duplicate-report-sidebar/duplicate-report-sidebar';
import { DuplicateTemplateSidebar } from '@futrli/predict/reports/components/duplicate-template-sidebar/duplicate-template-sidebar';
import {
  EmailPdfSidebar,
  EmailPdfSidebarData
} from '@futrli/predict/reports/components/email-pdf-sidebar/email-pdf-sidebar';
import { ExportReportSidebar } from '@futrli/predict/reports/components/export-report-sidebar/export-report-sidebar';
import { LiveReportList, ReportTemplateList } from '@futrli-sdk/predict';
import {
  ScheduleReportSidebar,
  ScheduleReportSidebarData
} from '@futrli/predict/reports/components/schedule-report-sidebar/schedule-report-sidebar';
import { ShareResourceSidebar } from '@futrli/predict/sharing/sidebars/share-resource-sidebar/share-resource-sidebar';
import { SharingEntityType } from '@futrli/predict/core/api/sharing';
import { useModal } from '@futrli/shared-services';
import {
  ViewPdfSidebar,
  ViewPdfSidebarData
} from '@futrli/predict/reports/components/view-pdf-sidebar/view-pdf-sidebar';

import { ForecastCreationSidebars } from './forecast-creation-sidebars';

interface SidebarControls<T = unknown> {
  close: () => void;
  data: T | undefined;
  isOpen: boolean;
  open: (data?: T) => void;
  setData: React.Dispatch<React.SetStateAction<T | undefined>>;
  toggle: (openState: boolean, data?: T) => void;
}

export interface CreateTemplateSidebarData {
  reportId: string;
}

export interface ShareResourceSidebarData {
  entityId: string;
  entityType: SharingEntityType;
  resourceName: string;
}

export interface SidebarContextValues {
  dashboard: SidebarControls;
  dashboardTemplate: SidebarControls<CreateTemplateSidebarData>;
  duplicateReport: SidebarControls<LiveReportList>;
  duplicateTemplate: SidebarControls<ReportTemplateList>;
  emailPdfReport: SidebarControls<EmailPdfSidebarData>;
  exportReport: SidebarControls<LiveReportList>;
  futrliTemplate: SidebarControls;
  pAndLBudget: SidebarControls;
  report: SidebarControls;
  shareResource: SidebarControls<ShareResourceSidebarData>;
  template: SidebarControls<CreateTemplateSidebarData>;
  threeWayForecast: SidebarControls;
  driverBuilder: SidebarControls<DriverBuilderSidebarData>;
  scheduleReport: SidebarControls<ScheduleReportSidebarData>;
  viewPdfReport: SidebarControls<ViewPdfSidebarData>;
}

interface SidebarContextProviderProps {
  children: ReactNode;
}

const defaultState = ({} as unknown) as SidebarContextValues;

export const SidebarContext = createContext(defaultState);

export const SidebarContextProvider = ({ children }: SidebarContextProviderProps) => {
  const dashboard = useModal();
  const dashboardTemplate = useModal<CreateTemplateSidebarData>();
  const duplicateReport = useModal<LiveReportList>();
  const duplicateTemplate = useModal<ReportTemplateList>();
  const exportReport = useModal<LiveReportList>();
  const emailPdfReport = useModal<EmailPdfSidebarData>();
  const futrliTemplate = useModal();
  const report = useModal();
  const shareResource = useModal<ShareResourceSidebarData>();
  const template = useModal<CreateTemplateSidebarData>();

  // Forecasting sidebars
  const pAndLBudget = useModal();
  const threeWayForecast = useModal();

  // Drivers sidebar
  const driverBuilder = useModal<DriverBuilderSidebarData>();

  // Schedule report sidebar
  const scheduleReport = useModal<ScheduleReportSidebarData>();

  // View PDF sidebar
  const viewPdfReport = useModal<ViewPdfSidebarData>();

  const value = {
    dashboard,
    dashboardTemplate,
    duplicateReport,
    duplicateTemplate,
    exportReport,
    emailPdfReport,
    futrliTemplate,
    pAndLBudget,
    report,
    shareResource,
    template,
    threeWayForecast,
    driverBuilder,
    scheduleReport,
    viewPdfReport
  };

  return (
    <SidebarContext.Provider value={value}>
      {children}
      <ForecastCreationSidebars
        isPAndLBudgetOpen={pAndLBudget.isOpen}
        isThreeWayForecastOpen={threeWayForecast.isOpen}
        onPAndLBudgetClose={pAndLBudget.close}
        onThreeWayForecastClose={threeWayForecast.close}
      />
      <CreateReportSidebar isOpen={report.isOpen} onClose={report.close} />
      <CreateReportSidebar isOpen={dashboard.isOpen} onClose={dashboard.close} mode="dashboard" />
      <CreateTemplateSidebar
        isOpen={template.isOpen}
        onClose={() => {
          template.close();
          template.setData(undefined);
        }}
        preSelectedReportId={template.data?.reportId}
      />
      <CreateTemplateSidebar
        isOpen={dashboardTemplate.isOpen}
        onClose={dashboardTemplate.close}
        mode="dashboard"
        preSelectedReportId={dashboardTemplate.data?.reportId}
      />
      <CreateFutrliTemplateSidebar isOpen={futrliTemplate.isOpen} onClose={futrliTemplate.close} />
      <DriverBuilderSidebar isOpen={driverBuilder.isOpen} onClose={driverBuilder.close} data={driverBuilder.data} />
      {duplicateReport.data && (
        <DuplicateReportSidebar
          isOpen={duplicateReport.isOpen}
          onClose={duplicateReport.close}
          report={duplicateReport.data}
        />
      )}
      {duplicateTemplate.data && (
        <DuplicateTemplateSidebar
          isOpen={duplicateTemplate.isOpen}
          onClose={duplicateTemplate.close}
          template={duplicateTemplate.data}
        />
      )}
      {exportReport.data && (
        <ExportReportSidebar isOpen={exportReport.isOpen} onClose={exportReport.close} report={exportReport.data} />
      )}
      {emailPdfReport.data && (
        <EmailPdfSidebar isOpen={emailPdfReport.isOpen} onClose={emailPdfReport.close} data={emailPdfReport.data} />
      )}
      {scheduleReport.data && (
        <ScheduleReportSidebar
          isOpen={scheduleReport.isOpen}
          onClose={scheduleReport.close}
          data={scheduleReport.data}
        />
      )}
      {viewPdfReport.data && (
        <ViewPdfSidebar
          isOpen={viewPdfReport.isOpen}
          onClose={viewPdfReport.close}
          report={viewPdfReport.data.report}
        />
      )}
      {shareResource.data && (
        <ShareResourceSidebar
          entityId={shareResource.data.entityId}
          entityType={shareResource.data.entityType}
          isOpen={shareResource.isOpen}
          onClose={shareResource.close}
          resourceName={shareResource.data.resourceName}
        />
      )}
    </SidebarContext.Provider>
  );
};
